import { defineField, defineType } from 'sanity'
import VehicleInput from '../../components/VehicleInput'

const getFilterType = (_type: string, title: string) => ({
  name: _type,
  title,
  type: 'object',
  fields: [
    defineField({
      name: 'initial',
      title: 'Initial Value',
      type: 'reference',
      to: [{ type: _type }],
    }),
    defineField({
      name: 'locked',
      title: 'Locked',
      description: `When locked, user can't change value of this filter. Even when gallery filters are reset, this filter will remain unchanged. Prepopulated filters will not be visible in the component, but it's value will be applied to the gallery.`,
      type: 'boolean',
      hidden: ({ parent }) => !parent?.initial?._ref,
      initialValue: false,
    }),
  ],
  preview: {
    select: {
      name: 'initial.name',
      locked: 'locked',
    },
    prepare({ name, locked }) {
      locked = name && locked
      name = name || 'Empty'

      return {
        title: locked ? `${name} (locked)` : name,
        subtitle: title,
      }
    },
  },
})

export default defineType({
  title: 'Vehicle gallery',
  name: 'vehicleGallery',
  type: 'object',
  fields: [
    defineField({
      name: 'title',
      title: 'Title',
      type: 'string',
    }),
    defineField({
      name: 'albumFilters',
      title: 'Album Filters',
      type: 'array',
      of: [
        getFilterType('color', 'Vehicle Color'),
        getFilterType('design', 'Design'),
        getFilterType('diameter', 'Diameter'),
        getFilterType('finish', 'Wheel Finish'),
        getFilterType('fitment', 'Fitment'),
        {
          name: 'line',
          title: 'Line',
          type: 'object',
          fields: [
            defineField({
              name: 'initial',
              title: 'Initial Value',
              type: 'reference',
              to: [{ type: 'line' }],
            }),
            defineField({
              name: 'locked',
              title: 'Locked',
              description: `When locked, user can't change value of this filter. Even when gallery filters are reset, this filter will remain unchanged. Prepopulated filters will not be visible in the component, but it's value will be applied to the gallery.`,
              type: 'boolean',
              hidden: ({ parent }) => !parent?.initial?._ref,
              initialValue: false,
            }),
          ],
          preview: {
            select: {
              name: 'initial.title',
              locked: 'locked',
            },
            prepare({ name, locked }) {
              locked = name && locked
              name = name || 'Empty'

              return {
                title: locked ? `${name} (locked)` : name,
                subtitle: 'Line',
              }
            },
          },
        },
        {
          type: 'object',
          name: 'vehicle',
          title: 'Vehicle',
          fields: [
            defineField({
              name: 'vehicle',
              title: 'Vehicle',
              type: 'object',
              fields: [
                defineField({
                  name: 'make',
                  type: 'reference',
                  to: [{ type: 'make' }],
                }),
                defineField({
                  name: 'model',
                  type: 'reference',
                  to: [{ type: 'model' }],
                }),
                defineField({
                  name: 'generation',
                  type: 'reference',
                  to: [{ type: 'generation' }],
                }),
                defineField({
                  name: 'submodel',
                  type: 'reference',
                  to: [{ type: 'submodel' }],
                }),
              ],
              components: {
                input: VehicleInput,
              },
            }),
            defineField({
              name: 'locked',
              title: 'Locked',
              description: `When locked, user can't change value of predefined filters. Even when gallery filters are reset, those filters will remain unchanged. Prepopulated filters will not be visible in the component, but it's value will be applied to the gallery.`,
              type: 'boolean',
              initialValue: false,
            }),
          ],
          preview: {
            select: {
              make: 'vehicle.make.name',
              model: 'vehicle.model.name',
              generation: 'vehicle.generation.name',
              submodel: 'vehicle.submodel.name',
            },
            prepare({ make = '', model = '', generation = '', submodel = '' }) {
              return {
                title:
                  [make, model, generation, submodel]
                    .filter((item) => !!item)
                    .join(', ') || 'Empty',
                subtitle: 'Vehicle',
              }
            },
          },
        },
      ],
      validation: (rule) =>
        rule.custom((items) => {
          // unique object type validation
          const uniqueLookup = items?.reduce((a: any, e: any) => {
            // count how many times each object type is used
            a[e?._type] = ++a[e?._type] || 0
            return a
          }, {})

          const duplicates = items?.reduce((map: any[], item: any) => {
            // check if object type is used more than once
            if (uniqueLookup[item?._type]) {
              map.push({ _key: item._key })
            }
            return map
          }, [])

          if (duplicates?.length > 0) {
            return {
              message: 'Each filter can be used only once',
              paths: duplicates,
            }
          }

          // every validation passed
          return true
        }),
    }),
    defineField({
      name: 'albumsPerPage',
      title: 'Albums per page',
      description: 'Number of images visible on single page.',
      type: 'number',
      options: {
        list: [
          { title: '6', value: 6 },
          { title: '12', value: 12 },
          { title: '18', value: 18 },
        ],
        layout: 'dropdown',
        direction: 'horizontal',
      },
      initialValue: 6,
      validation: (rule) => rule.required(),
    }),
  ],
  preview: {
    select: {
      title: 'title',
    },
    prepare({ title }) {
      return {
        title,
        subtitle: 'Vehicle gallery',
      }
    },
  },
})